/* eslint-disable react/prop-types */
import React from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Box, Card, Chip, Typography } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import Swal from 'sweetalert2';
import {
  ALLOWED_TRANSITIONS,
  GUEST_CHIP_COLOR,
  GUEST_CHIP_LABEL,
  KANBAN_COLUMNS,
  STATUS_HEX,
  formatAppointmentDateTime,
  formatAppointmentNo,
  resolveDoctorName,
  resolvePatientMobile,
  resolvePatientName,
} from '../constants';

function AppointmentCard({ appointment, onSelect }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: appointment.id,
    data: { appointment },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={() => onSelect(appointment.id)}
      sx={{
        p: 1.5,
        mb: 1.25,
        borderRadius: 4,
        cursor: 'grab',
        borderLeft: `4px solid ${STATUS_HEX[appointment.status] || '#90a4ae'}`,
        '&:hover': { boxShadow: 3 },
      }}
    >
      <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', fontWeight: 700, color: STATUS_HEX[appointment.status] }}>
        {formatAppointmentNo(appointment)}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#2d3748' }}>
          {resolvePatientName(appointment, appointment.irm_contacts_id)}
        </Typography>
        {appointment.is_guest === 'Y' && (
          <Chip label={GUEST_CHIP_LABEL} size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: GUEST_CHIP_COLOR, color: '#fff' }} />
        )}
      </Box>
      {resolvePatientMobile(appointment) && (
        <Box display="flex" alignItems="center" gap={0.3}>
          <PhoneIcon sx={{ fontSize: 11, color: '#78909c' }} />
          <Typography sx={{ fontSize: '0.7rem', color: '#78909c' }}>
            {resolvePatientMobile(appointment)}
          </Typography>
        </Box>
      )}
      <Typography sx={{ fontSize: '0.72rem', color: '#78909c' }}>
        {resolveDoctorName(appointment.doctorInfo, appointment.irm_users_id)}
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: '#aaa' }}>
        {formatAppointmentDateTime(appointment.appointment_date, appointment.appointment_time)} · #{appointment.serial_no}
      </Typography>
    </Card>
  );
}

function KanbanColumn({ column, appointments, onSelect }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.status });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: '1 1 200px',
        minWidth: 200,
        bgcolor: isOver ? `${STATUS_HEX[column.status]}18` : '#fafafa',
        borderRadius: 2,
        border: `1px solid ${isOver ? STATUS_HEX[column.status] : '#e0e0e0'}`,
        p: 1,
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} px={0.5}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: STATUS_HEX[column.status] }}>
          {column.label}
        </Typography>
        <Chip
          label={appointments.length}
          size="small"
          sx={{ height: 18, fontSize: '0.65rem', bgcolor: STATUS_HEX[column.status], color: 'white' }}
        />
      </Box>
      {appointments.map(appointment => (
        <AppointmentCard key={appointment.id} appointment={appointment} onSelect={onSelect} />
      ))}
    </Box>
  );
}

// onStatusChange(appointmentId, fromStatus, toStatus) is invoked only after the
// drag lands on a column that represents a genuinely allowed transition
// (checked client-side against ALLOWED_TRANSITIONS) — the parent is
// responsible for the Swal confirm + actual API call + refresh.
export default function AppointmentKanbanBoard({ appointments, onSelect, onStatusChange }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const byStatus = KANBAN_COLUMNS.reduce((acc, column) => {
    acc[column.status] = appointments.filter(a => a.status === column.status);
    return acc;
  }, {});

  const handleDragEnd = event => {
    const { active, over } = event;
    if (!over) return;

    const appointment = active.data.current?.appointment;
    const targetStatus = over.id;
    if (!appointment || appointment.status === targetStatus) return;

    const allowed = ALLOWED_TRANSITIONS[appointment.status] || [];
    if (!allowed.includes(targetStatus)) {
      Swal.fire({
        icon: 'warning',
        title: 'Not allowed',
        text: `An appointment cannot move from ${appointment.status} to ${targetStatus} directly.`,
        timer: 2200,
        showConfirmButton: false,
      });
      return;
    }

    // A doctor can only have one patient ATTENDED at a time on a given day —
    // block the drag client-side too, before even opening the confirm dialog
    // (the server enforces the same rule regardless).
    if (targetStatus === 'ATTENDED') {
      const conflict = appointments.find(
        a =>
          a.id !== appointment.id &&
          a.irm_users_id === appointment.irm_users_id &&
          a.appointment_date === appointment.appointment_date &&
          a.status === 'ATTENDED',
      );
      if (conflict) {
        Swal.fire({
          icon: 'warning',
          title: 'Doctor is busy',
          html: `This doctor is already attending <b>${resolvePatientName(conflict, conflict.irm_contacts_id)}</b> (serial #${conflict.serial_no}) today.<br/>Mark that one Completed first.`,
        });
        return;
      }
    }

    onStatusChange(appointment.id, appointment.status, targetStatus);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
        {KANBAN_COLUMNS.map(column => (
          <KanbanColumn
            key={column.status}
            column={column}
            appointments={byStatus[column.status] || []}
            onSelect={onSelect}
          />
        ))}
      </Box>
      <DragOverlay />
    </DndContext>
  );
}
